import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { scroll } from "../base/scroll";
import { varGutter, prefersReducedMotion } from "../utils/variables";
import { mouse, initMouse } from "../utils/mouse";
import { isWebglEnabled } from "./isWebglEnabled";
import { getRingGltf, constructedRingAsset, IPart, IStream, streamWidthScale, streamGapStartScale, streamGapEndScale, constructedRingStartRadius, constructedRingEndRadius, createDrumLogoMaterial, setMaterialColor, setMaterialOpacity, findMeshByObjectName, updateRingStreamWidth, createRingPart, clearStreams, getCustomLogoText, loadTextRingFont, createTextSourceMesh, innerTextCopies, outerTextCopies, getDefaultInnerRing } from "./logo-ring";

interface ILogoHomeHitAreaMetrics {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface ILogoRenderRegion {
    left: number;
    top: number;
    width: number;
    height: number;
}

interface LogoInstance {
    dispose: () => void;
    rebindRoot: (newRoot: HTMLElement) => void;
    setFrozen: (frozen: boolean) => void;
    snapshot: () => Promise<Blob | null>;
}

let updateLogoPageRef: ((root: HTMLElement) => void) | null = null;

export function updateLogoPage(root: HTMLElement) {
    updateLogoPageRef?.(root);
}

const logoInstances = new Map<HTMLElement, LogoInstance>();

export function getLogoInstance(root: HTMLElement) {
    return logoInstances.get(root);
}

export default function initLogo(root: HTMLElement) {
    if (logoInstances.has(root)) return logoInstances.get(root);

    const container = root.querySelector<HTMLElement>(".logo");
    if (!container || !isWebglEnabled()) return;

    let activeRoot = root;

    let heroWordmark = activeRoot.querySelector<HTMLElement>(".hero__wordmark");

    //////// CORE
    //////// PAGE CONTEXT
    let hasLoader = document.documentElement.classList.contains("show-loader");
    let hasHero = Boolean(activeRoot.querySelector(".hero"));
    const isHomeTransitionIn = !hasLoader && hasHero && document.documentElement.hasAttribute("data-navigating");
    let heroEl = activeRoot.querySelector<HTMLElement>(".hero");
    let detailHeroEl = activeRoot.querySelector<HTMLElement>(".detail-hero");
    let cachedHeaderEl = activeRoot.querySelector<HTMLElement>("header[data-header]:not([hidden])");

    //////// CORE
    const scene = new THREE.Scene();
    const clock = new THREE.Clock();
    const renderScale = 2;
    const hiddenTabFrameStride = 8;
    const activeFrameIntervalMs = 1000 / 60;
    const settledFrameIntervalMs = 1000 / 20;
    const frameIntervalToleranceMs = 4;
    const settledTiltThreshold = 0.0005;
    const logoStateRegionPaddingPx = 24;

    //////// CAMERA
    const introDisabledFov = 30;
    const introDisabledZ = 1.45;
    const introCameraStartZ = 2.35;
    const mobileMarqueeFov = 22;
    const mobileLayoutBreakpoint = 900;

    //////// ANIMATION
    const spinSpeed = 0.1;
    const scrollSpinVelocityFactor = 0.205;
    const scrollSpinBoostMax = 20;
    const scrollSpinSmoothing = 100;
    const colorChangeSpinTurns = 0.2;
    const colorChangeSpinDurationMs = 1000;
    const preIntroStartRotationDegrees = 40;
    const introSpinTurns = 1;
    const introAnimationDurationMs = 2000;
    const outerCopyIntroRepeatProgress = 0.38;
    const innerRingIntroDelaySeconds = 1;
    const wheelSpinVelocityScale = 0.15;
    const wheelSpinDecay = 10;

    let wheelSpinVelocity = 0;

    //////// ANIMATE OUT
    const introSwitchEnabled = true;
    const introSwitchPoint = { type: "degrees", value: -30 };

    //////// RESTING TILT
    const baseTiltX = THREE.MathUtils.degToRad(-2.5);
    const baseTiltZ = THREE.MathUtils.degToRad(3);
    const secondaryRotationXAmount = THREE.MathUtils.degToRad(12);
    const secondaryRotationZAmount = THREE.MathUtils.degToRad(8);
    const secondaryRotationSpeed = 0.1;
    const cursorTiltXAmount = THREE.MathUtils.degToRad(12);
    const cursorTiltZAmount = THREE.MathUtils.degToRad(8);
    const cursorTiltSmoothing = 12;
    const logoStateTiltAmount = 0.35;

    //////// OVERALL SCALE
    const constructedRingStartScale = 0.4;
    const constructedRingEndScale = 0.7;
    const mobileConstructedRingStartScale = constructedRingStartScale * 0.5;
    const mobileConstructedRingEndScale = constructedRingEndScale * 0.5;

    //////// MARQUEE PHASE
    let marqueePhaseEnabled = hasHero;
    let isScrollDriven = !hasHero;
    const homeLoaderMarqueeLeadMs = 0;
    const homeTransitionMarqueeDelayMs = 300;
    let marqueePhaseDelayMs = getInitialMarqueePhaseDelayMs();
    const marqueePhaseDurationMs = 800;
    const marqueeOuterScale = 4.5;
    const marqueeOuterDepthScale = 0.01;
    const marqueeBottomPaddingViewport = 0.01;
    const marqueeSpinSpeed = 0.05;
    const marqueeTransitionSpinTurns = 0.5;
    const heroMarqueeToLogoDeadZone = 0;
    const mobileMarqueeModelScale = 2.1;

    function getInitialMarqueePhaseDelayMs() {
        if (!hasHero) return 0;
        if (isHomeTransitionIn) return introAnimationDurationMs + homeTransitionMarqueeDelayMs;
        if (hasLoader) return Math.max(introAnimationDurationMs - homeLoaderMarqueeLeadMs, 0);

        return 500;
    }

    //////// RINGS
    //////// LOGO STATE (top-left ring)
    const logoStateScale = 0.2;
    const scrollLogoRevealDurationMs = 500;
    const logoStateGutterXScale = 1.5;
    const logoStateGutterYScale = -0.8;
    const logoColorSwitchThreshold = 0.85;
    const innerRingRevealProgress = 0.45;
    const innerRingDepthScaleStart = 0.12;
    const innerRingZOffsetStart = 0.45;
    const logoStateHitAreaScale = 1.25;
    const logoStateHitAreaOffsetY = 6;
    const logoStateHitAreaMinSize = 32;
    const logoLightColor = 0xffffff;
    const logoDarkColor = 0x000000;
    const mobileMarqueeDepthScale = 1;
    const mobileMarqueeBottomPaddingViewport = 0.1;
    const mobileMarqueeGapRatio = 2.3;
    const mobileMarqueeWidthFill = 4;

    //////// CONTENT
    const activeInnerRing = getDefaultInnerRing();
    const outerRingName = "BROTHER3";
    const outerRingCopies = 3;
    const filmRingName = "FILM3";
    const filmRingCopies = 6;
    const musicRingName = "MUSIC3";
    const musicRingCopies = 5;
    const innerRingOptions: Record<string, { name: string; copies: number }> = {
        film: { name: filmRingName, copies: filmRingCopies },
        music: { name: musicRingName, copies: musicRingCopies },
    };

    //////// STATE
    let model: THREE.Group | null = null;
    let modelMarquee: THREE.Group | null = null;
    let modelSpin: THREE.Group | null = null;
    let ringRoot: THREE.Group | null = null;
    let sourceScene: THREE.Group | null = null;
    let startupSpinStartTime: number | null = null;
    let startupSpinLastAngle = 0;
    let marqueeSpinLastAngle = 0;
    let startupSpinFinished = false;
    let constructedRingIntroFinished = false;
    let introSwitchProgress = 0;
    let lastEasedVisualIntroProgress = 0;
    let currentInnerRing = activeInnerRing;
    let currentOuterText = "";
    let currentInnerText = "";
    let marqueePauseStartTime: number | null = null;
    let marqueePausedDurationMs = 0;
    let isMobileLayout: boolean = getIsMobileLayout();

    let ringRebuildId = 0;
    let waitingForStart = !isScrollDriven;
    let logoTransitionProgress = 0;
    let scrollLogoOpen = false;
    let scrollLogoTweenStartTime: number | null = null;
    let scrollLogoTweenFrom = 0;
    let currentScrollSpinBoost = 0;
    let currentLogoColor = logoDarkColor;
    let colorChangeSpinStartTime: number | null = null;
    let colorChangeSpinLastAngle = 0;
    const colorChangeSpinDirection = -1;
    let chipTurnTarget = 0;
    let chipTurnAngle = 0;
    let chipTurnVelocity = 0;
    let hasNotifiedLoaderSequenceComplete = false;
    let hasResolvedLogoColor = false;
    let targetCursorTiltX = 0;
    let targetCursorTiltZ = 0;
    let currentCursorTiltX = 0;
    let currentCursorTiltZ = 0;
    let logoHomeHitAreaMetrics: ILogoHomeHitAreaMetrics | null = null;
    let logoHomeHitAreaDirty = true;
    let logoHomeHitAreaActive = false;
    let lastAppliedHitAreaMetrics: ILogoHomeHitAreaMetrics | null = null;
    let isDocumentHidden = document.hidden;
    let hiddenFrameCounter = 0;
    let lastRenderedFrameTime = -Infinity;
    let isCursorTiltSettled = true;
    let hasMergedLogoStreams = false;
    let isNavigating = false;
    let isFrozenBg = false;
    let hasClearedHiddenLogo = false;
    let appliedRenderRegion: ILogoRenderRegion | null = null;
    let marqueeRenderRegion: ILogoRenderRegion | null = null;
    let marqueeWarpBand: ILogoRenderRegion | null = null;
    let marqueeWarp = 0;
    let isDisposed = false;

    const onNavStart = () => {
        isNavigating = true;
    };
    const onNavEnd = () => {
        isNavigating = false;
        currentScrollSpinBoost = 0;
        wheelSpinVelocity = 0;
    };

    window.addEventListener("barba:nav-start", onNavStart);
    window.addEventListener("barba:nav-end", onNavEnd);

    const constructedRingStreams: IStream[] = [];
    const tempLogoCenter = new THREE.Vector3();
    const tempLogoRight = new THREE.Vector3();
    const tempLogoWorldPosition = new THREE.Vector3();
    const tempRegionBox = new THREE.Box3();
    const tempRegionMeshBox = new THREE.Box3();
    const tempRegionCorner = new THREE.Vector3();

    const camera = new THREE.PerspectiveCamera(introDisabledFov, container.clientWidth / container.clientHeight, 0.001, 200);
    camera.position.set(0, 0, introCameraStartZ);

    function getRenderPixelRatio() {
        return Math.min(window.devicePixelRatio || 1, renderScale);
    }

    initMouse();

    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true, powerPreference: "low-power" });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.toneMappingExposure = 1;
    renderer.setPixelRatio(getRenderPixelRatio());
    renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(renderer.domElement);

    const antialiasTarget = new THREE.WebGLRenderTarget(1, 1, { samples: 4, colorSpace: THREE.SRGBColorSpace, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
    const antialiasCopyScene = new THREE.Scene();
    const antialiasCopyCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const antialiasCopyMesh = new THREE.Mesh(
        new THREE.PlaneGeometry(2, 2),
        new THREE.ShaderMaterial({
            uniforms: {
                tMap: { value: antialiasTarget.texture },
                uWarp: { value: 0 },
                uRegionSize: { value: new THREE.Vector2(1, 1) },
                uBand: { value: new THREE.Vector2(0.5, 1) },
                uCursor: { value: new THREE.Vector2(0, 0) },
                uCursorStrength: { value: 0 },
                uGutter: { value: 1 },
                uBump: { value: 0 },
            },
            vertexShader: `
                varying vec2 vUv;

                void main() {
                    vUv = uv;
                    gl_Position = vec4(position.xy, 0.0, 1.0);
                }
            `,
            fragmentShader: `
                uniform sampler2D tMap;
                uniform float uWarp;
                uniform vec2 uRegionSize;
                uniform vec2 uBand;
                uniform vec2 uCursor;
                uniform float uCursorStrength;
                uniform float uGutter;
                uniform float uBump;
                varying vec2 vUv;

                vec4 sampleWarped() {
                    if (uWarp <= 0.0) return texture2D(tMap, vUv);

                    vec2 px = vUv * uRegionSize;
                    float halfWidth = uRegionSize.x * 0.5;
                    float bandHeight = uBand.y;
                    float side = sign(px.x - halfWidth);
                    vec2 toCursor = (px - uCursor) / (bandHeight * 0.5);
                    float nearCursor = uCursorStrength * exp(-dot(toCursor, toCursor) * 2.5);
                    float lipSize = uGutter * (1.0 + uBump * 3.0);
                    float edge = (halfWidth - abs(px.x - halfWidth)) / lipSize;
                    float swell = uWarp * (1.0 + nearCursor);
                    float lip = pow(smoothstep(1.0, 0.0, edge), 3.0) * swell;
                    float bow = pow(smoothstep(1.0, 0.0, edge), 2.0) * swell;
                    px.x -= side * lip * 0.6 * lipSize;
                    px.y = uBand.x + (px.y - uBand.x) * (1.0 - bow * 0.06);
                    vec2 uv = px / uRegionSize;
                    vec2 split = vec2(side * lip * 0.25 * lipSize / uRegionSize.x, 0.0);

                    vec4 texR = texture2D(tMap, uv + split);
                    vec4 texG = texture2D(tMap, uv);
                    vec4 texB = texture2D(tMap, uv - split);
                    return vec4(min(vec3(texR.r, texG.g, texB.b), vec3(texG.a)), texG.a);
                }

                void main() {
                    vec4 texel = sampleWarped();
                    vec3 color = texel.a > 0.0 ? texel.rgb / texel.a : vec3(0.0);
                    gl_FragColor = vec4(linearToOutputTexel(vec4(color, 1.0)).rgb * texel.a, texel.a);
                }
            `,
            blending: THREE.NoBlending,
            depthTest: false,
            depthWrite: false,
        })
    );
    antialiasCopyMesh.frustumCulled = false;
    antialiasCopyScene.add(antialiasCopyMesh);

    const logoStateLink = document.createElement("a");
    logoStateLink.href = "/";
    logoStateLink.className = "logo__home-hit";
    logoStateLink.setAttribute("aria-label", "Go to homepage");
    logoStateLink.setAttribute("data-cursor", "-link");
    logoStateLink.setAttribute("data-logo-home-hit", "");
    container.appendChild(logoStateLink);

    function updateCursorTiltTarget(clientX: number, clientY: number) {
        const nx = THREE.MathUtils.clamp((clientX / window.innerWidth) * 2 - 1, -1, 1);
        const ny = THREE.MathUtils.clamp((clientY / window.innerHeight) * 2 - 1, -1, 1);

        targetCursorTiltX = -ny * cursorTiltXAmount;
        targetCursorTiltZ = nx * cursorTiltZAmount;
        isCursorTiltSettled = false;
    }

    function onSpinWheel(e: WheelEvent) {
        wheelSpinVelocity += e.deltaY * wheelSpinVelocityScale;
    }

    window.addEventListener("wheel", onSpinWheel, { passive: true });

    function onLogoMouseMove(event: MouseEvent) {
        updateCursorTiltTarget(event.clientX, event.clientY);
    }

    function onLogoMouseLeave() {
        targetCursorTiltX = 0;
        targetCursorTiltZ = 0;
    }

    function onLogoChipTurn(event: Event) {
        if (isNavigating || prefersReducedMotion) return;

        const { direction } = (event as CustomEvent<{ direction: number }>).detail;
        const quarterTurn = Math.PI / 2;

        chipTurnTarget = THREE.MathUtils.clamp(chipTurnTarget - direction * quarterTurn, chipTurnAngle - quarterTurn * 1.5, chipTurnAngle + quarterTurn * 1.5);
    }

    window.addEventListener("mousemove", onLogoMouseMove);
    window.addEventListener("mouseleave", onLogoMouseLeave);
    window.addEventListener("hero:chip-turn", onLogoChipTurn);
    function onLogoPointerDown(event: PointerEvent) {
        if ((event.target as HTMLElement | null)?.closest("a[data-hero-preview]")) {
            isNavigating = true;
        }
    }

    window.addEventListener("pointerdown", onLogoPointerDown, true);

    //////// RESPONSIVE STATE
    function getIsMobileLayout() {
        return window.matchMedia(`(max-width: ${mobileLayoutBreakpoint}px)`).matches;
    }

    function getActiveMarqueeSettings() {
        return isMobileLayout
            ? {
                  outerScale: mobileMarqueeModelScale,
                  depthScale: mobileMarqueeDepthScale,
                  bottomPaddingViewport: mobileMarqueeBottomPaddingViewport,
              }
            : {
                  outerScale: marqueeOuterScale,
                  depthScale: marqueeOuterDepthScale,
                  bottomPaddingViewport: marqueeBottomPaddingViewport,
              };
    }

    function getActiveIntroScaleRange() {
        return isMobileLayout
            ? {
                  start: mobileConstructedRingStartScale,
                  end: mobileConstructedRingEndScale,
              }
            : {
                  start: constructedRingStartScale,
                  end: constructedRingEndScale,
              };
    }

    //////// CREATING THE SCENE-GRAPH HIERARCHY
    function setLoadedModel(root: THREE.Group) {
        model = new THREE.Group();
        modelMarquee = new THREE.Group();
        modelSpin = new THREE.Group();
        modelSpin.rotation.y = THREE.MathUtils.degToRad(preIntroStartRotationDegrees);
        ringRoot = root;
        modelSpin.add(ringRoot);
        modelMarquee.add(modelSpin);
        model.add(modelMarquee);
        scene.add(model);
    }

    //////// MEASURING THE RING AGAINST THE VIEWPORT
    function getViewportWidthAtOrigin() {
        return getViewportHeightAtOrigin() * camera.aspect;
    }

    function getStreamSpanX(stream: IStream) {
        let minX = Infinity;
        let maxX = -Infinity;

        stream.meshes.forEach((mesh: THREE.Mesh) => {
            const bounds = mesh.geometry.boundingBox;

            if (!bounds) {
                return;
            }

            minX = Math.min(minX, bounds.min.x);
            maxX = Math.max(maxX, bounds.max.x);
        });

        return Number.isFinite(minX) && Number.isFinite(maxX) ? maxX - minX : 0;
    }

    //////// CUSTOM RING TEXT — only applied when the full-page checkbox is on.
    function readCustomLogoText() {
        const custom = getCustomLogoText(activeRoot);

        currentOuterText = custom.fullPage ? custom.outer : "";
        currentInnerText = custom.fullPage ? custom.inner : "";
    }

    function needsTextRingFont() {
        return Boolean(currentOuterText || currentInnerText);
    }

    function getConstructedRingParts() {
        const innerRingOption = innerRingOptions[currentInnerRing] || innerRingOptions.film;
        const outerCopies = currentOuterText ? outerTextCopies : outerRingCopies;
        const innerCopies = currentInnerText ? innerTextCopies : innerRingOption.copies;

        return [
            {
                name: outerRingName,
                text: currentOuterText,
                copies: outerCopies,
                isInnerRing: false,
                introRepeatCopies: outerCopies > 1,
            },
            {
                ...innerRingOption,
                text: currentInnerText,
                copies: innerCopies,
                isInnerRing: true,
            },
        ];
    }

    function clearConstructedRingStreams() {
        clearStreams(constructedRingStreams);
        hasMergedLogoStreams = false;
    }

    function mergeStreamsForSteadyState() {
        if (hasMergedLogoStreams) {
            return;
        }

        constructedRingStreams.forEach(stream => {
            if (stream.isMerged || stream.meshes.length <= 1) {
                return;
            }

            const geometries = stream.meshes.map(mesh => {
                mesh.updateMatrix();
                const geometry = mesh.geometry.clone();
                geometry.applyMatrix4(mesh.matrix);
                return geometry;
            });

            const mergedGeometry = mergeGeometries(geometries, false);
            geometries.forEach(geometry => geometry.dispose());

            if (!mergedGeometry) {
                return;
            }

            const mergedMesh = new THREE.Mesh(mergedGeometry, stream.material);

            mergedMesh.name = `${stream.name}_merged`;

            stream.meshes.forEach(mesh => {
                stream.group.remove(mesh);
                mesh.geometry.dispose();
            });

            stream.meshes.length = 0;
            stream.group.add(mergedMesh);
            stream.mergedMesh = mergedMesh;
            stream.isMerged = true;
        });

        hasMergedLogoStreams = true;
    }

    //////// BUILDING THE FULL DRUM GROUP
    function buildConstructedRingGroup(parts: IPart[]) {
        const group = new THREE.Group();

        parts.forEach(part => {
            const sourceMesh = part.text ? createTextSourceMesh(part.text, `${part.name}_TEXT`, part.copies, part.isInnerRing, currentLogoColor, renderer.capabilities.getMaxAnisotropy()) : findMeshByObjectName(sourceScene!, part.name);

            if (!sourceMesh) {
                console.error(`Missing mesh "${part.name}" in ${constructedRingAsset}`);
                return;
            }

            const built = createRingPart(sourceMesh, part, constructedRingStartRadius, part.text ? sourceMesh.material : createDrumLogoMaterial(currentLogoColor));

            constructedRingStreams.push(built.stream);
            group.add(built.group);
        });

        return group;
    }

    function applyCurrentRingState() {
        const introProgress = getIntroProgress(performance.now());
        const marqueeProgress = getMarqueeProgress(performance.now());

        constructedRingIntroFinished = false;
        updateIntroSwitchState(introProgress, marqueeProgress);
        updateConstructedRingIntro(lastEasedVisualIntroProgress, true);
        updateMarqueePhase(easeOutCubic(marqueeProgress));
        updateHeroWordmarkHeight();
    }

    function rebuildConstructedRing() {
        if (!sourceScene) {
            return Promise.resolve();
        }

        const rebuildId = ++ringRebuildId;

        return loadTextRingFont(needsTextRingFont())
            .then(() => {
                if (isDisposed || rebuildId !== ringRebuildId) {
                    return;
                }

                if (modelSpin && ringRoot) {
                    modelSpin.remove(ringRoot);
                }

                clearConstructedRingStreams();

                const newRingRoot = buildConstructedRingGroup(getConstructedRingParts());

                ringRoot = newRingRoot;

                if (modelSpin) {
                    modelSpin.add(ringRoot);
                    applyCurrentRingState();
                }
            })
            .catch(console.error);
    }

    function clamp01(value: number) {
        return Math.min(Math.max(value, 0), 1);
    }

    function getClockwiseDegreesToTarget(startDegrees: number, targetDegrees: number) {
        const distance = (((startDegrees - targetDegrees) % 360) + 360) % 360;

        return distance === 0 ? 360 : distance;
    }

    function getIntroSwitchProgress() {
        if (!introSwitchEnabled) {
            return 0;
        }

        if (introSwitchPoint.type === "time") {
            return clamp01(introSwitchPoint.value > 1 ? introSwitchPoint.value / introAnimationDurationMs : introSwitchPoint.value);
        }

        const clockwiseDegrees = getClockwiseDegreesToTarget(preIntroStartRotationDegrees, introSwitchPoint.value);
        const totalStartupDegrees = Math.max(Math.abs(introSpinTurns) * 360, 1);

        return clamp01(clockwiseDegrees / totalStartupDegrees);
    }

    //////// LOADING THE GLB AND STARTING THE DRUM
    function loadConstructedRing() {
        readCustomLogoText();
        Promise.all([loadTextRingFont(needsTextRingFont()), getRingGltf()])
            .then(([, gltf]) => {
                if (isDisposed) return;

                sourceScene = gltf.scene;

                introSwitchProgress = getIntroSwitchProgress();

                setLoadedModel(buildConstructedRingGroup(getConstructedRingParts()));
                updateHeroWordmarkHeight();
            })
            .catch(error => {
                console.error(error);
                if (hasLoader && !hasNotifiedLoaderSequenceComplete) {
                    hasNotifiedLoaderSequenceComplete = true;
                    container!.classList.remove("logo--loader");
                    window.dispatchEvent(new CustomEvent("logo:loader-sequence-complete"));
                }
            });
    }

    loadConstructedRing();

    let startRafId = 0;
    let startTimeoutId = 0;

    function startAfterHeroFrame() {
        if (!waitingForStart || startRafId) return;

        startRafId = requestAnimationFrame(() => {
            startRafId = requestAnimationFrame(() => {
                waitingForStart = false;
                clock.getDelta();
            });
        });
    }

    if (waitingForStart) {
        window.addEventListener("hero:first-frame", startAfterHeroFrame, { once: true });
        startTimeoutId = window.setTimeout(startAfterHeroFrame, 4000);
    }

    function easeOutCubic(t: number) {
        return 1 - Math.pow(1 - t, 3);
    }

    function easeIntro(t: number) {
        return t < 0.5 ? 64 * t * t * t * t * t * t * t : 1 - Math.pow(-2 * t + 2, 7) / 2;
    }

    function updateScrollSpinBoost(delta: number) {
        wheelSpinVelocity *= Math.exp(-delta * wheelSpinDecay);

        const lenisVelocity = scroll?.lenisInstance?.velocity ?? 0;
        const rawVelocity = isNavigating || isFrozenBg ? 0 : Math.abs(wheelSpinVelocity) > Math.abs(lenisVelocity) ? wheelSpinVelocity : lenisVelocity;

        const targetBoost = THREE.MathUtils.clamp(rawVelocity * scrollSpinVelocityFactor, -scrollSpinBoostMax, scrollSpinBoostMax);
        const smoothing = 1 - Math.exp(-delta * scrollSpinSmoothing);

        currentScrollSpinBoost = THREE.MathUtils.lerp(currentScrollSpinBoost, targetBoost, smoothing);
    }

    function getIntroMatchedSpinAngle(progress: number, elapsedMs: number) {
        const startupDuration = introAnimationDurationMs * 0.001;
        const elapsedSeconds = Math.min(elapsedMs, introAnimationDurationMs) * 0.001;
        const restingSpinAngle = -spinSpeed * elapsedSeconds;
        const startupSpinAngleTarget = introSpinTurns * Math.PI * 2;
        const startupSpinBoost = Math.max(startupSpinAngleTarget - spinSpeed * startupDuration, 0);

        return restingSpinAngle - startupSpinBoost * easeIntro(progress);
    }

    //////// APPLYING SUBTLE RESTING TILT
    function getColorChangeSpinDelta(now: number) {
        if (colorChangeSpinStartTime === null) {
            return 0;
        }

        const progress = clamp01((now - colorChangeSpinStartTime) / colorChangeSpinDurationMs);
        const easedProgress = easeOutCubic(progress);
        const nextAngle = colorChangeSpinDirection * colorChangeSpinTurns * Math.PI * 2 * easedProgress;
        const delta = nextAngle - colorChangeSpinLastAngle;

        colorChangeSpinLastAngle = nextAngle;

        if (progress >= 1) {
            colorChangeSpinStartTime = null;
            colorChangeSpinLastAngle = 0;
        }

        return delta;
    }

    function getChipTurnDelta(delta: number) {
        if (chipTurnAngle === chipTurnTarget && chipTurnVelocity === 0) {
            return 0;
        }

        const stiffness = 10;
        const offset = chipTurnAngle - chipTurnTarget;
        const impulse = (chipTurnVelocity + stiffness * offset) * delta;
        const damping = Math.exp(-stiffness * delta);
        let nextAngle = chipTurnTarget + (offset + impulse) * damping;

        chipTurnVelocity = (chipTurnVelocity - stiffness * impulse) * damping;

        if (Math.abs(nextAngle - chipTurnTarget) < 0.0005 && Math.abs(chipTurnVelocity) < 0.005) {
            nextAngle = chipTurnTarget;
            chipTurnVelocity = 0;
        }

        const turnDelta = nextAngle - chipTurnAngle;
        chipTurnAngle = nextAngle;

        return turnDelta;
    }

    function updateRestingTilt(now: number, delta: number) {
        if (!model) {
            return;
        }

        const marqueeProgress = easeOutCubic(getMarqueeProgress(now));
        const logoBlend = easeOutCubic(logoTransitionProgress);

        const baseTiltAmount = 1 - marqueeProgress;
        const tiltAmount = THREE.MathUtils.lerp(baseTiltAmount, logoStateTiltAmount, logoBlend);

        if (isMobileLayout && marqueeProgress > 0) {
            model.rotation.x = 0;
            model.rotation.z = 0;
            return;
        }

        // Idle floating animation
        const time = now * -0.001 * secondaryRotationSpeed;
        const idleTiltX = Math.sin(time) * secondaryRotationXAmount;
        const idleTiltZ = Math.cos(time * 0.8) * secondaryRotationZAmount;

        // Cursor tilt
        const smoothing = 1 - Math.exp(-delta * cursorTiltSmoothing);
        const allowCursorTilt = startupSpinFinished && logoTransitionProgress >= logoColorSwitchThreshold && (!hasHero || marqueeProgress >= 1);

        const goalTiltX = allowCursorTilt ? targetCursorTiltX : 0;
        const goalTiltZ = allowCursorTilt ? targetCursorTiltZ : 0;

        currentCursorTiltX = THREE.MathUtils.lerp(currentCursorTiltX, goalTiltX, smoothing);
        currentCursorTiltZ = THREE.MathUtils.lerp(currentCursorTiltZ, goalTiltZ, smoothing);
        isCursorTiltSettled = Math.abs(goalTiltX - currentCursorTiltX) < settledTiltThreshold && Math.abs(goalTiltZ - currentCursorTiltZ) < settledTiltThreshold;

        model.rotation.x = (baseTiltX + idleTiltX + currentCursorTiltX) * tiltAmount;
        model.rotation.z = (baseTiltZ + idleTiltZ + currentCursorTiltZ) * tiltAmount;
    }

    function getIntroProgress(now: number) {
        if (startupSpinStartTime === null) {
            return 0;
        }

        const introElapsed = now - startupSpinStartTime;

        return Math.min(introElapsed / introAnimationDurationMs, 1);
    }

    function getVisualIntroProgress(introProgress: number) {
        if (!introSwitchEnabled) {
            return introProgress;
        }

        const switchProgress = THREE.MathUtils.clamp(introSwitchProgress, 0, 0.95);

        return clamp01((introProgress - switchProgress) / (1 - switchProgress));
    }

    function getMarqueeProgress(now: number) {
        if (!marqueePhaseEnabled || startupSpinStartTime === null) {
            return 0;
        }

        const activePauseDuration = marqueePauseStartTime === null ? 0 : now - marqueePauseStartTime;
        const totalPausedDuration = marqueePausedDurationMs + activePauseDuration;
        const marqueeElapsed = Math.max(now - startupSpinStartTime - totalPausedDuration - marqueePhaseDelayMs, 0);

        return Math.min(marqueeElapsed / marqueePhaseDurationMs, 1);
    }

    function getHeroScrollProgress() {
        if (!hasHero || !heroEl) {
            return 0;
        }

        const heroRect = heroEl.getBoundingClientRect();
        const heroHeight = Math.max(heroRect.height, 1);

        return clamp01(-heroRect.top / heroHeight);
    }

    //////// PLACING THE MARQUEE AT THE BOTTOM OF THE VIEWPORT
    function getViewportHeightAtOrigin() {
        return 2 * Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5)) * camera.position.z;
    }

    function getMarqueeTargetY(outerScale: number, bottomPaddingViewport: number) {
        const targetStream = constructedRingStreams.find(stream => !stream.isInnerRing);

        if (!targetStream || !modelSpin) {
            return 0;
        }

        const viewportHeight = getViewportHeightAtOrigin();
        const viewportBottom = viewportHeight * -0.5;
        const bottomPadding = viewportHeight * bottomPaddingViewport;
        const scaledRingBottom = targetStream.sourceBounds.min.y * modelSpin.scale.y * outerScale;

        return viewportBottom + bottomPadding - scaledRingBottom;
    }

    function updateHeroWordmarkHeight() {
        if (!heroWordmark) {
            return;
        }

        const outerStream = constructedRingStreams.find(stream => !stream.isInnerRing);

        if (!outerStream) {
            heroWordmark.style.removeProperty("height");
            return;
        }

        const streamHeightWorld = Math.max(outerStream.sourceBounds.max.y - outerStream.sourceBounds.min.y, 0);
        const marqueeHeightWorld = streamHeightWorld * constructedRingEndScale * marqueeOuterScale;
        const marqueeViewportHeightWorld = Math.max(2 * Math.tan(THREE.MathUtils.degToRad(introDisabledFov * 0.5)) * introDisabledZ, 0.0001);
        const pixelsPerWorldUnit = container!.clientHeight / marqueeViewportHeightWorld;
        const marqueeHeightPx = Math.max(marqueeHeightWorld * pixelsPerWorldUnit, 0);

        heroWordmark.style.height = `${marqueeHeightPx.toFixed(2)}px`;
    }

    //////// REVEALING EXTRA COPIES AND INNER RING DURING INTRO
    function updateOuterCopyIntroRepeat(introProgress: number) {
        const shouldRepeatCopies = introProgress >= outerCopyIntroRepeatProgress;

        constructedRingStreams.forEach(stream => {
            if (stream.introRepeatCopies) {
                stream.repeatCopiesVisible = shouldRepeatCopies;
            }
        });
    }

    function updateIntroSwitchState(introProgress: number, marqueeProgress: number) {
        const hasPassedInnerRingDelay = introProgress >= clamp01(innerRingIntroDelaySeconds / (introAnimationDurationMs * 0.001));

        updateOuterCopyIntroRepeat(introProgress);

        constructedRingStreams.forEach(stream => {
            if (stream.isInnerRing) {
                stream.group.visible = hasPassedInnerRingDelay && marqueeProgress === 0;
            } else {
                stream.group.visible = true;
            }
        });
    }

    //////// MOVING THE CAMERA THROUGH INTRO
    function updateCameraIntro(easedProgress: number) {
        const marqueeProgress = isMobileLayout ? easeOutCubic(getMarqueeProgress(performance.now())) : 0;

        camera.fov = THREE.MathUtils.lerp(introDisabledFov, mobileMarqueeFov, marqueeProgress);
        camera.position.z = THREE.MathUtils.lerp(introCameraStartZ, introDisabledZ, easedProgress);
        camera.updateProjectionMatrix();
    }

    //////// ANIMATING RING RADIUS, WIDTH, GAP AND SCALE
    function updateConstructedRingIntro(easedProgress: number, force: boolean) {
        lastEasedVisualIntroProgress = easedProgress;

        if (constructedRingStreams.length === 0 || (constructedRingIntroFinished && !force)) {
            return;
        }

        const gapScale = THREE.MathUtils.lerp(streamGapStartScale, streamGapEndScale, easedProgress);
        const radius = THREE.MathUtils.lerp(constructedRingStartRadius, constructedRingEndRadius, easedProgress);
        const introScaleRange = getActiveIntroScaleRange();
        const overallScale = THREE.MathUtils.lerp(introScaleRange.start, introScaleRange.end, easedProgress);

        constructedRingStreams.forEach(stream => {
            updateRingStreamWidth(stream, streamWidthScale, gapScale, radius);
        });

        if (modelSpin) {
            modelSpin.scale.setScalar(overallScale);
        }

        if (easedProgress >= 1) {
            constructedRingIntroFinished = true;
        }
    }

    //////// RE-FITTING THE MOBILE MARQUEE RING
    function updateMobileMarqueeRing(easedProgress: number) {
        if (!isMobileLayout) {
            return;
        }

        const mobileMarqueeStream = constructedRingStreams.find(stream => !stream.isInnerRing);

        if (!mobileMarqueeStream) {
            return;
        }

        const sourceWidth = mobileMarqueeStream.sourceBounds.max.x - mobileMarqueeStream.sourceBounds.min.x;
        const targetGap = sourceWidth * mobileMarqueeGapRatio;
        const targetRadius = (sourceWidth + targetGap) / (Math.PI * 2);
        const radius = THREE.MathUtils.lerp(constructedRingEndRadius, targetRadius, easedProgress);
        const gapScale = 1;

        updateRingStreamWidth(mobileMarqueeStream, 1, gapScale, radius);
    }

    function getLogoTransitionProgressFromHeroScroll() {
        if (!heroEl) {
            return 0;
        }

        const heroRect = heroEl.getBoundingClientRect();
        const heroHeight = Math.max(heroRect.height, 1);
        const rawProgress = clamp01(-heroRect.top / heroHeight);
        const deadZone = THREE.MathUtils.clamp(heroMarqueeToLogoDeadZone, 0, 0.95);

        if (rawProgress <= deadZone) {
            return 0;
        }

        // Keep marquee pinned during the dead zone, then remap remaining scroll to 0..1.
        return clamp01((rawProgress - deadZone) / (1 - deadZone));
    }

    function isHeaderScrolledOut() {
        if (!cachedHeaderEl) return false;
        return cachedHeaderEl.getBoundingClientRect().bottom <= 0;
    }

    function projectWorldPointToScreen(worldPoint: THREE.Vector3) {
        const projected = worldPoint.clone().project(camera);

        if (!Number.isFinite(projected.x) || !Number.isFinite(projected.y)) {
            return null;
        }

        return {
            x: (projected.x + 1) * 0.5 * container!.clientWidth,
            y: (1 - projected.y) * 0.5 * container!.clientHeight,
        };
    }

    function getLogoHitAreaMetrics() {
        const centerWorld = getLogoWorldPosition();
        const ringRadiusWorld = constructedRingEndRadius * constructedRingEndScale * logoStateScale;

        tempLogoCenter.copy(centerWorld);
        tempLogoRight.copy(centerWorld);
        tempLogoRight.x += ringRadiusWorld;

        const centerScreen = projectWorldPointToScreen(tempLogoCenter);
        const rightScreen = projectWorldPointToScreen(tempLogoRight);

        if (!centerScreen || !rightScreen) {
            return null;
        }

        const radiusPx = Math.abs(rightScreen.x - centerScreen.x);
        const size = Math.max(radiusPx * 2 * logoStateHitAreaScale, logoStateHitAreaMinSize);

        return {
            x: centerScreen.x,
            y: centerScreen.y + logoStateHitAreaOffsetY,
            width: size,
            height: size,
        };
    }

    function isLogoSettled(now: number) {
        const lenisVelocity = Math.abs(scroll?.lenisInstance?.velocity ?? 0);
        const isSpinBoosted = Math.abs(currentScrollSpinBoost) > 0.001 || chipTurnAngle !== chipTurnTarget || chipTurnVelocity !== 0 || Math.abs(wheelSpinVelocity) > 0.5 || colorChangeSpinStartTime !== null;

        return isLogoState(now) && isCursorTiltSettled && scrollLogoTweenStartTime === null && lenisVelocity < 0.01 && !isSpinBoosted;
    }

    function isLogoState(now: number, marqueeProgressOverride: number | null = null) {
        const marqueeProgress = marqueeProgressOverride ?? getMarqueeProgress(now);

        return startupSpinFinished && logoTransitionProgress >= logoColorSwitchThreshold && (!hasHero || marqueeProgress >= 1);
    }

    function getLogoStateRenderRegion(now: number): ILogoRenderRegion | null {
        if (!isLogoState(now)) {
            return null;
        }

        const metrics = logoHomeHitAreaMetrics || getLogoHitAreaMetrics();
        if (!metrics) {
            return null;
        }

        const fullWidth = container!.clientWidth;
        const fullHeight = container!.clientHeight;
        const left = THREE.MathUtils.clamp(Math.floor(metrics.x - metrics.width * 0.5 - logoStateRegionPaddingPx), 0, fullWidth);
        const top = THREE.MathUtils.clamp(Math.floor(metrics.y - metrics.height * 0.5 - logoStateRegionPaddingPx), 0, fullHeight);
        const right = THREE.MathUtils.clamp(Math.ceil(metrics.x + metrics.width * 0.5 + logoStateRegionPaddingPx), 0, fullWidth);
        const bottom = THREE.MathUtils.clamp(Math.ceil(metrics.y + metrics.height * 0.5 + logoStateRegionPaddingPx), 0, fullHeight);

        if (right <= left || bottom <= top) {
            return null;
        }

        return { left, top, width: right - left, height: bottom - top };
    }

    function getMarqueeRenderRegion(now: number): ILogoRenderRegion | null {
        const isMarqueeShown = marqueePhaseEnabled && !isMobileLayout && getMarqueeProgress(now) >= 1 && !!model;
        const isSettledMarquee = isMarqueeShown && logoTransitionProgress === 0;

        marqueeWarp = isMarqueeShown && marqueeWarpBand ? 1 - THREE.MathUtils.smoothstep(logoTransitionProgress, 0, 0.12) : 0;

        if (!isSettledMarquee) {
            marqueeRenderRegion = null;
            if (marqueeWarp <= 0) return null;

            return { left: 0, top: 0, width: container!.clientWidth, height: container!.clientHeight };
        }

        marqueeRenderRegion ??= getProjectedModelBand(model!);
        marqueeWarpBand = marqueeRenderRegion;
        return marqueeRenderRegion;
    }

    function getProjectedModelBand(target: THREE.Group): ILogoRenderRegion | null {
        target.updateWorldMatrix(true, true);
        tempRegionBox.makeEmpty();

        target.traverseVisible(object => {
            if (!(object instanceof THREE.Mesh)) return;

            if (!object.geometry.boundingBox) object.geometry.computeBoundingBox();
            tempRegionMeshBox.copy(object.geometry.boundingBox!).applyMatrix4(object.matrixWorld);
            tempRegionBox.union(tempRegionMeshBox);
        });

        if (tempRegionBox.isEmpty()) {
            return null;
        }

        const fullWidth = container!.clientWidth;
        const fullHeight = container!.clientHeight;
        let top = fullHeight;

        for (const x of [tempRegionBox.min.x, tempRegionBox.max.x]) {
            for (const y of [tempRegionBox.min.y, tempRegionBox.max.y]) {
                for (const z of [tempRegionBox.min.z, tempRegionBox.max.z]) {
                    tempRegionCorner.set(x, y, z).project(camera);
                    top = Math.min(top, (1 - tempRegionCorner.y) * 0.5 * fullHeight);
                }
            }
        }

        const regionTop = THREE.MathUtils.clamp(Math.floor(top - logoStateRegionPaddingPx), 0, fullHeight);

        return { left: 0, top: regionTop, width: fullWidth, height: fullHeight - regionTop };
    }

    function isSameRenderRegion(a: ILogoRenderRegion | null, b: ILogoRenderRegion | null) {
        if (!a || !b) {
            return a === b;
        }

        return a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height;
    }

    function updateRenderRegion(now: number) {
        const logoStateRegion = getLogoStateRenderRegion(now);
        if (logoStateRegion) marqueeWarp = 0;

        const region = logoStateRegion ?? getMarqueeRenderRegion(now);
        if (isSameRenderRegion(region, appliedRenderRegion)) {
            return;
        }

        appliedRenderRegion = region;

        const fullWidth = container!.clientWidth;
        const fullHeight = container!.clientHeight;
        const canvas = renderer.domElement;

        if (!region) {
            renderer.setSize(fullWidth, fullHeight);
            canvas.style.left = "0px";
            canvas.style.top = "0px";
            return;
        }

        const viewportY = region.top + region.height - fullHeight;

        renderer.setSize(region.width, region.height);
        canvas.style.left = `${region.left}px`;
        canvas.style.top = `${region.top}px`;

        const pixelRatio = renderer.getPixelRatio();
        antialiasTarget.setSize(canvas.width, canvas.height);
        antialiasTarget.viewport.set(-region.left, viewportY, fullWidth, fullHeight).multiplyScalar(pixelRatio).floor();
    }

    function syncMarqueeWarp() {
        const uniforms = (antialiasCopyMesh.material as THREE.ShaderMaterial).uniforms;
        const region = appliedRenderRegion;
        const band = marqueeWarpBand;

        uniforms.uWarp.value = region && band ? marqueeWarp : 0;
        if (!region || !band || marqueeWarp <= 0) return;

        const regionBottom = region.top + region.height;
        const isInside = mouse.x >= band.left && mouse.x <= band.left + band.width && mouse.y >= band.top && mouse.y <= band.top + band.height;
        const cursor = uniforms.uCursor.value as THREE.Vector2;

        uniforms.uGutter.value = Math.max(varGutter, 1);
        uniforms.uBump.value = prefersReducedMotion ? 0 : Math.min(Math.abs(currentScrollSpinBoost) / 6, 1) + Math.min(Math.abs(chipTurnVelocity) / 6, 1) * 2;
        (uniforms.uRegionSize.value as THREE.Vector2).set(region.width, region.height);
        (uniforms.uBand.value as THREE.Vector2).set(regionBottom - (band.top + band.height * 0.5), band.height);
        cursor.x += (mouse.x - region.left - cursor.x) * 0.2;
        cursor.y += (regionBottom - mouse.y - cursor.y) * 0.2;
        uniforms.uCursorStrength.value += ((isInside && !prefersReducedMotion ? 1 : 0) - uniforms.uCursorStrength.value) * 0.1;
    }

    function renderLogoFrame() {
        if (!appliedRenderRegion) {
            renderer.render(scene, camera);
            return;
        }

        syncMarqueeWarp();
        renderer.setRenderTarget(antialiasTarget);
        renderer.render(scene, camera);
        renderer.setRenderTarget(null);
        renderer.render(antialiasCopyScene, antialiasCopyCamera);
    }

    function updateLogoHomeHitArea(now: number) {
        const marqueeProgress = getMarqueeProgress(now);
        const inLogoState = isLogoState(now, marqueeProgress);

        if (!inLogoState) {
            logoStateLink.classList.remove("is-active");
            logoHomeHitAreaActive = false;
            lastAppliedHitAreaMetrics = null;
            return;
        }

        if (!logoHomeHitAreaActive) {
            logoHomeHitAreaDirty = true;
        }

        if (logoHomeHitAreaDirty || !logoHomeHitAreaMetrics) {
            logoHomeHitAreaMetrics = getLogoHitAreaMetrics();
            logoHomeHitAreaDirty = false;
        }

        if (!logoHomeHitAreaMetrics) {
            logoStateLink.classList.remove("is-active");
            return;
        }

        logoHomeHitAreaActive = true;

        logoStateLink.classList.add("is-active");

        if (lastAppliedHitAreaMetrics !== logoHomeHitAreaMetrics) {
            lastAppliedHitAreaMetrics = logoHomeHitAreaMetrics;
            logoStateLink.style.left = `${logoHomeHitAreaMetrics.x}px`;
            logoStateLink.style.top = `${logoHomeHitAreaMetrics.y}px`;
            logoStateLink.style.width = `${logoHomeHitAreaMetrics.width}px`;
            logoStateLink.style.height = `${logoHomeHitAreaMetrics.height}px`;
        }
    }

    function isInHomeHero() {
        if (!hasHero || !heroEl) {
            return false;
        }

        const heroRect = heroEl.getBoundingClientRect();

        return heroRect.bottom > 50 && heroRect.top < window.innerHeight;
    }

    function isInDetailHero() {
        if (!detailHeroEl) {
            return false;
        }

        const rect = detailHeroEl.getBoundingClientRect();

        return rect.bottom > 50 && rect.top < window.innerHeight;
    }

    function resolveLogoColor() {
        if (hasResolvedLogoColor && document.documentElement.hasAttribute("data-navigating")) {
            return currentLogoColor;
        }
        const inIntro = waitingForStart || startupSpinStartTime === null || !startupSpinFinished;

        if (inIntro) {
            return logoLightColor;
        }

        const isDarkPage = document.body.getAttribute("data-theme") === "dark";

        if (isDarkPage || isInHomeHero() || isInDetailHero()) {
            return logoLightColor;
        }

        return logoDarkColor;
    }

    function updateLogoColor() {
        const nextColor = resolveLogoColor();
        hasResolvedLogoColor = true;

        if (nextColor === currentLogoColor) {
            return;
        }

        currentLogoColor = nextColor;

        constructedRingStreams.forEach(stream => {
            setMaterialColor(stream.material, currentLogoColor);
        });
    }

    function getLogoWorldPosition() {
        const viewportHeight = getViewportHeightAtOrigin();
        const viewportWidth = viewportHeight * (container!.clientWidth / container!.clientHeight);
        const gutterPx = Math.max(varGutter || 0, 0);
        const gutterWorldX = ((gutterPx * logoStateGutterXScale) / Math.max(container!.clientWidth, 1)) * viewportWidth;
        const gutterWorldY = ((gutterPx * logoStateGutterYScale) / Math.max(container!.clientHeight, 1)) * viewportHeight;
        const ringRadius = constructedRingEndRadius * constructedRingEndScale * logoStateScale;

        return tempLogoWorldPosition.set(-viewportWidth * 0.5 + gutterWorldX + ringRadius, viewportHeight * 0.5 - gutterWorldY - ringRadius, 0);
    }

    function updateLogoState(progress: number) {
        if (!model || !modelMarquee) return;

        const eased = easeOutCubic(progress);
        const innerRevealEased = easeOutCubic(clamp01(progress / Math.max(innerRingRevealProgress, 0.0001)));
        const fromScale = marqueePhaseEnabled ? marqueeOuterScale : 1;
        const fromDepth = marqueePhaseEnabled ? marqueeOuterDepthScale : 1;
        const fromY = marqueePhaseEnabled ? getMarqueeTargetY(marqueeOuterScale, marqueeBottomPaddingViewport) : 0;

        constructedRingStreams.forEach(stream => {
            if (!stream.isInnerRing) return;

            stream.group.visible = progress > 0;
            stream.group.scale.set(1, 1, THREE.MathUtils.lerp(innerRingDepthScaleStart, 1, innerRevealEased));
            stream.group.position.z = THREE.MathUtils.lerp(innerRingZOffsetStart, 0, innerRevealEased);
            setMaterialOpacity(stream.material, innerRevealEased);
        });

        modelMarquee.scale.set(THREE.MathUtils.lerp(fromScale, 1, eased), THREE.MathUtils.lerp(fromScale, 1, eased), THREE.MathUtils.lerp(fromDepth, 1, eased));
        modelMarquee.position.y = THREE.MathUtils.lerp(fromY, 0, eased);

        const targetPosition = getLogoWorldPosition();

        model.position.set(THREE.MathUtils.lerp(0, targetPosition.x, eased), THREE.MathUtils.lerp(0, targetPosition.y, eased), 0);
        model.scale.setScalar(THREE.MathUtils.lerp(1, logoStateScale, eased));
    }

    function updateScrollLogoState(progress: number) {
        if (!model || !modelMarquee) return;

        const targetPosition = getLogoWorldPosition();

        modelMarquee.scale.set(1, 1, 1);
        modelMarquee.position.y = 0;
        model.position.set(targetPosition.x, targetPosition.y, 0);
        model.scale.setScalar(logoStateScale * progress);

        constructedRingStreams.forEach(stream => {
            if (!stream.isInnerRing) return;
            stream.group.visible = progress > 0;
            stream.group.scale.set(1, 1, 1);
            stream.group.position.z = 0;
            setMaterialOpacity(stream.material, progress);
        });
    }

    //////// MOVING THE DRUM INTO MARQUEE POSITION
    function updateMarqueePhase(easedProgress: number) {
        if (!modelMarquee) {
            return;
        }

        if (easedProgress <= 0) {
            modelMarquee.position.y = 0;
            modelMarquee.scale.set(1, 1, 1);
            return;
        }

        updateMobileMarqueeRing(easedProgress);

        const marqueeSettings = getActiveMarqueeSettings();
        let targetOuterScale = marqueeSettings.outerScale;

        if (isMobileLayout) {
            const mobileMarqueeStream = constructedRingStreams.find(stream => !stream.isInnerRing);

            if (mobileMarqueeStream && modelSpin) {
                const streamSpanX = getStreamSpanX(mobileMarqueeStream);
                const viewportWidth = getViewportWidthAtOrigin() * mobileMarqueeWidthFill;
                const spinScale = modelSpin.scale.x || 1;

                if (streamSpanX > 0 && spinScale > 0) {
                    targetOuterScale = viewportWidth / (streamSpanX * spinScale);
                }
            }
        }

        const outerScale = THREE.MathUtils.lerp(1, targetOuterScale, easedProgress);
        const depthScale = THREE.MathUtils.lerp(1, marqueeSettings.depthScale, easedProgress);
        const y = THREE.MathUtils.lerp(0, getMarqueeTargetY(outerScale, marqueeSettings.bottomPaddingViewport), easedProgress);

        modelMarquee.position.y = y;
        modelMarquee.scale.set(outerScale, outerScale, depthScale);
    }

    function onLogoVisibilityChange() {
        isDocumentHidden = document.hidden;
        hiddenFrameCounter = 0;
        renderer.setPixelRatio(isDocumentHidden ? 1 : getRenderPixelRatio());
        appliedRenderRegion = null;
        marqueeRenderRegion = null;
        marqueeWarpBand = null;
    }

    document.addEventListener("visibilitychange", onLogoVisibilityChange);

    //////// SHARED: WINDOW RESIZE AND RESPONSIVE SYNC
    function onLogoResize() {
        const nextIsMobileLayout = getIsMobileLayout();

        const w = container!.clientWidth;
        const h = container!.clientHeight;

        if (nextIsMobileLayout !== isMobileLayout) {
            isMobileLayout = nextIsMobileLayout;
            constructedRingIntroFinished = false;
            updateConstructedRingIntro(lastEasedVisualIntroProgress, true);
            updateMarqueePhase(easeOutCubic(getMarqueeProgress(performance.now())));
        }

        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setPixelRatio(getRenderPixelRatio());
        renderer.setSize(w, h);
        appliedRenderRegion = null;
        marqueeRenderRegion = null;
        marqueeWarpBand = null;
        logoHomeHitAreaDirty = true;
        logoHomeHitAreaMetrics = null;
        updateHeroWordmarkHeight();
    }

    window.addEventListener("resize", onLogoResize);

    function updatePage() {
        if (isHomeTransitionIn && waitingForStart) return;

        const namespace = activeRoot.getAttribute("data-barba-namespace");

        heroWordmark = activeRoot.querySelector<HTMLElement>(".hero__wordmark");
        hasHero = Boolean(activeRoot.querySelector(".hero"));
        heroEl = hasHero ? activeRoot.querySelector<HTMLElement>(".hero") : null;
        detailHeroEl = activeRoot.querySelector<HTMLElement>(".detail-hero");
        cachedHeaderEl = activeRoot.querySelector<HTMLElement>("header[data-header]:not([hidden])");

        marqueePhaseEnabled = hasHero;
        isScrollDriven = !hasHero;
        hasLoader = false;
        marqueePhaseDelayMs = hasHero ? 500 : 0;

        const newInnerRing = namespace === "work-detail-music" ? "music" : getDefaultInnerRing();
        const previousOuterText = currentOuterText;
        const previousInnerText = currentInnerText;

        readCustomLogoText();

        if (newInnerRing !== currentInnerRing || currentOuterText !== previousOuterText || currentInnerText !== previousInnerText) {
            currentInnerRing = newInnerRing;
            rebuildConstructedRing();
        }

        const now = performance.now();
        startupSpinFinished = true;
        hasNotifiedLoaderSequenceComplete = true;
        marqueePauseStartTime = null;
        marqueePausedDurationMs = 0;

        if (hasHero) {
            startupSpinStartTime = now - introAnimationDurationMs - marqueePhaseDelayMs - marqueePhaseDurationMs;
            marqueeSpinLastAngle = -marqueeTransitionSpinTurns * Math.PI * 2;
        } else {
            startupSpinStartTime = now - introAnimationDurationMs;
        }

        if (isScrollDriven) {
            const headerOutNow = isHeaderScrolledOut();
            scrollLogoOpen = headerOutNow;
            logoTransitionProgress = headerOutNow ? 1 : 0;
        } else {
            logoTransitionProgress = 0;
            scrollLogoOpen = false;
        }
        scrollLogoTweenStartTime = null;
        scrollLogoTweenFrom = 0;

        updateHeroWordmarkHeight();
        updateLogoColor();

        logoHomeHitAreaDirty = true;
        logoHomeHitAreaMetrics = null;
        logoHomeHitAreaActive = false;
    }

    updateLogoPageRef = updatePage;

    //////// SHARED: MAIN RENDER LOOP
    function animate() {
        const now = performance.now();

        if (isDocumentHidden) {
            hiddenFrameCounter = (hiddenFrameCounter + 1) % hiddenTabFrameStride;
            if (hiddenFrameCounter !== 0) {
                return;
            }
        }

        const frameIntervalMs = isLogoSettled(now) ? settledFrameIntervalMs : activeFrameIntervalMs;

        if (now - lastRenderedFrameTime < frameIntervalMs - frameIntervalToleranceMs) {
            return;
        }

        const delta = clock.getDelta();

        if (isFrozenBg) {
            return;
        }

        lastRenderedFrameTime = now;

        updateScrollSpinBoost(delta);

        if (modelSpin) {
            if (startupSpinStartTime === null && !waitingForStart) {
                if (isScrollDriven) {
                    startupSpinStartTime = now - introAnimationDurationMs;
                    startupSpinFinished = true;
                    if (hasLoader && !hasNotifiedLoaderSequenceComplete) {
                        hasNotifiedLoaderSequenceComplete = true;
                        container!.classList.remove("logo--loader");
                        window.dispatchEvent(new CustomEvent("logo:loader-sequence-complete"));
                    }
                } else if (hasLoader) {
                    startupSpinStartTime = now;
                } else if (hasHero) {
                    startupSpinStartTime = now - introAnimationDurationMs;
                    startupSpinFinished = true;
                }
            }

            const introProgress = getIntroProgress(now);
            const easedVisualIntroProgress = easeIntro(introProgress);
            const marqueeProgress = getMarqueeProgress(now);
            const easedMarqueeProgress = easeOutCubic(marqueeProgress);

            if (marqueePhaseEnabled && marqueeProgress >= 1 && heroEl) {
                logoTransitionProgress = getLogoTransitionProgressFromHeroScroll();
            } else if (isScrollDriven && startupSpinFinished) {
                const headerOut = isHeaderScrolledOut();
                if (headerOut !== scrollLogoOpen) {
                    scrollLogoOpen = headerOut;
                    scrollLogoTweenFrom = logoTransitionProgress;
                    scrollLogoTweenStartTime = now;
                }
                if (scrollLogoTweenStartTime !== null) {
                    const t = clamp01((now - scrollLogoTweenStartTime!) / scrollLogoRevealDurationMs);
                    const target = scrollLogoOpen ? 1 : 0;
                    logoTransitionProgress = THREE.MathUtils.lerp(scrollLogoTweenFrom, target, easeOutCubic(t));
                    if (t >= 1) scrollLogoTweenStartTime = null;
                }
            }

            if (!startupSpinFinished) {
                const startupElapsed = now - (startupSpinStartTime ?? now);
                const startupSpinAngle = getIntroMatchedSpinAngle(introProgress, startupElapsed);
                const startupSpinDelta = startupSpinAngle - startupSpinLastAngle;
                startupSpinLastAngle = startupSpinAngle;

                modelSpin!.rotation.y += startupSpinDelta;

                if (introProgress >= 1) {
                    startupSpinFinished = true;

                    if (!hasHero) {
                        logoTransitionProgress = 0;
                    } else if (getHeroScrollProgress() > 0) {
                        startupSpinStartTime = now - introAnimationDurationMs - marqueePhaseDelayMs - marqueePhaseDurationMs;
                        marqueeSpinLastAngle = -marqueeTransitionSpinTurns * Math.PI * 2;
                        logoTransitionProgress = getLogoTransitionProgressFromHeroScroll();
                    }

                    if (hasLoader && !hasNotifiedLoaderSequenceComplete) {
                        hasNotifiedLoaderSequenceComplete = true;
                        container!.classList.remove("logo--loader");
                        window.dispatchEvent(new CustomEvent("logo:loader-sequence-complete"));
                    }
                }
            } else {
                const baseSpinSpeed = THREE.MathUtils.lerp(spinSpeed, marqueeSpinSpeed, easedMarqueeProgress);
                const activeSpinSpeed = baseSpinSpeed + currentScrollSpinBoost;
                const marqueeSpinAngle = -marqueeTransitionSpinTurns * Math.PI * 2 * easedMarqueeProgress;
                const marqueeSpinDelta = marqueeSpinAngle - marqueeSpinLastAngle;
                marqueeSpinLastAngle = marqueeSpinAngle;

                const ongoingSpinDelta = -delta * activeSpinSpeed + marqueeSpinDelta;

                modelSpin!.rotation.y += ongoingSpinDelta;
            }

            updateRestingTilt(now, delta);
            updateIntroSwitchState(introProgress, marqueeProgress);
            updateConstructedRingIntro(easedVisualIntroProgress, false);
            updateMarqueePhase(easedMarqueeProgress);

            const colorChangeSpinDelta = getColorChangeSpinDelta(now);
            modelSpin!.rotation.y += colorChangeSpinDelta;
            modelSpin!.rotation.y += getChipTurnDelta(delta);
        }

        if (isScrollDriven) {
            updateScrollLogoState(logoTransitionProgress);
        } else if (logoTransitionProgress > 0) {
            updateLogoState(logoTransitionProgress);
        } else if (model) {
            model!.position.set(0, 0, 0);
            model!.scale.setScalar(1);
        }

        if (!hasMergedLogoStreams && constructedRingIntroFinished && isLogoState(now)) {
            mergeStreamsForSteadyState();
        }

        updateLogoHomeHitArea(now);

        updateLogoColor();

        const cameraIntroProgress = startupSpinStartTime !== null ? easeIntro(getVisualIntroProgress(getIntroProgress(now))) : hasLoader ? 0 : 1;
        updateCameraIntro(cameraIntroProgress);

        updateRenderRegion(now);

        const isLogoHidden = !model || model.scale.x === 0;

        if (isLogoHidden && hasClearedHiddenLogo) {
            return;
        }

        hasClearedHiddenLogo = isLogoHidden;

        renderLogoFrame();
    }

    let rafId = requestAnimationFrame(function tick() {
        rafId = requestAnimationFrame(tick);
        animate();
    });

    function dispose() {
        isDisposed = true;
        cancelAnimationFrame(rafId);
        cancelAnimationFrame(startRafId);
        window.clearTimeout(startTimeoutId);
        window.removeEventListener("wheel", onSpinWheel);
        window.removeEventListener("barba:nav-start", onNavStart);
        window.removeEventListener("barba:nav-end", onNavEnd);
        window.removeEventListener("hero:first-frame", startAfterHeroFrame);
        window.removeEventListener("mousemove", onLogoMouseMove);
        window.removeEventListener("mouseleave", onLogoMouseLeave);
        window.removeEventListener("hero:chip-turn", onLogoChipTurn);
        window.removeEventListener("resize", onLogoResize);
        window.removeEventListener("pointerdown", onLogoPointerDown, true);
        document.removeEventListener("visibilitychange", onLogoVisibilityChange);
        clearConstructedRingStreams();
        antialiasTarget.dispose();
        antialiasCopyMesh.geometry.dispose();
        antialiasCopyMesh.material.dispose();
        if (updateLogoPageRef === updatePage) updateLogoPageRef = null;
        renderer.forceContextLoss();
        renderer.dispose();
        renderer.domElement.remove();
        logoStateLink.remove();
        logoInstances.delete(activeRoot);
    }

    function rebindRoot(newRoot: HTMLElement) {
        logoInstances.delete(activeRoot);
        activeRoot = newRoot;
        heroWordmark = newRoot.querySelector<HTMLElement>(".hero__wordmark");
        heroEl = newRoot.querySelector<HTMLElement>(".hero");
        detailHeroEl = newRoot.querySelector<HTMLElement>(".detail-hero");
        cachedHeaderEl = newRoot.querySelector<HTMLElement>("header[data-header]:not([hidden])");

        logoInstances.set(newRoot, instance);
    }

    function setFrozen(frozen: boolean) {
        isFrozenBg = frozen;
    }

    function snapshot() {
        renderLogoFrame();
        return new Promise<Blob | null>(resolve => renderer.domElement.toBlob(resolve));
    }

    const instance: LogoInstance = { dispose, rebindRoot, setFrozen, snapshot };
    logoInstances.set(activeRoot, instance);
    return instance;
}
